import {FlagProps} from 'platform/components';
import {match, Pattern} from 'ts-pattern';

import {useMemo} from 'react';

import {always} from 'ramda';

import i18n from '@dms/i18n';

import {Nullish} from 'shared/web';

export type ServiceStateType =
  | 'DEMAND'
  | 'DRAFT'
  | 'HANDED_OVER'
  | 'PLANNED'
  | 'CANCELLED'
  | 'CHECKED_IN'
  | 'CREATED';

export type OrderStateType =
  | 'CANCELLED'
  | 'CLOSED'
  | 'CORRECTING_FULLY'
  | 'CORRECTING_PARTIALLY'
  | 'IN_PROGRESS'
  | 'INPROGRESS'
  | 'ARCHIVED'
  | 'LOCKED'
  | 'OFFER'
  | 'OPEN'
  | 'PAUSED'
  | 'PLANNED'
  | 'REQUEST'
  | 'WORKDONE'
  | 'WORK_DONE'
  | 'DRAFT'
  | 'INVOICED'
  | 'POSTPONED';

export type JobStateType =
  | 'ARCHIVED'
  | 'CANCELLED'
  | 'DRAFT'
  | 'REQUEST'
  | 'OFFER'
  | 'OPEN'
  | 'INPROGRESS'
  | 'PAUSED'
  | 'WORKDONE'
  | 'LOCKED'
  | 'CLOSED'
  | 'POSTPONED'
  | 'SVCPOSTPONEJOB_POSTPONE'
  | 'SVCPOSTPONEJOB_DONE'
  | 'SVCPOSTPONEJOB_CANCELLED';

export type StateType = ServiceStateType | OrderStateType | JobStateType;

const getStateFlag = (state: string | Nullish): FlagProps | undefined =>
  match<string | Nullish, FlagProps | undefined>(state)
    .with(
      'ARCHIVED',
      always({label: i18n.t('entity.serviceOrder.states.archived'), colorScheme: 'neutral'})
    )
    .with(
      'CLOSED',
      always({label: i18n.t('entity.serviceOrder.states.closed'), colorScheme: 'green'})
    )
    .with(
      'CORRECTING_FULLY',
      always({
        label: i18n.t('entity.serviceOrder.states.correctingFully'),
        colorScheme: 'orange',
      })
    )
    .with(
      'CORRECTING_PARTIALLY',
      always({
        label: i18n.t('entity.serviceOrder.states.correctingPartially'),
        colorScheme: 'yellow',
      })
    )
    .with(
      'DRAFT',
      always({label: i18n.t('entity.serviceOrder.states.draft'), colorScheme: 'neutral'})
    )
    .with(
      'INPROGRESS',
      'IN_PROGRESS',
      always({label: i18n.t('entity.serviceOrder.states.inProgress'), colorScheme: 'blue'})
    )
    .with(
      'INVOICED',
      always({label: i18n.t('entity.serviceOrder.states.invoiced'), colorScheme: 'green'})
    )
    .with(
      'LOCKED',
      always({label: i18n.t('entity.serviceOrder.states.locked'), colorScheme: 'green'})
    )
    .with('OFFER', always({label: i18n.t('entity.serviceOrder.states.offer'), colorScheme: 'blue'}))
    .with('OPEN', always({label: i18n.t('entity.serviceOrder.states.open'), colorScheme: 'blue'}))
    .with(
      'PAUSED',
      always({label: i18n.t('entity.serviceOrder.states.paused'), colorScheme: 'orange'})
    )
    .with(
      'PLANNED',
      always({label: i18n.t('entity.serviceOrder.states.planned'), colorScheme: 'orange'})
    )
    .with(
      'REQUEST',
      'DEMAND',
      always({label: i18n.t('entity.serviceOrder.states.request'), colorScheme: 'blue'})
    )
    .with(
      'WORKDONE',
      'WORK_DONE',
      always({label: i18n.t('entity.serviceOrder.states.workDone'), colorScheme: 'green'})
    )
    .with(
      'SVCPOSTPONEJOB_POSTPONE',
      'POSTPONED',
      always({label: i18n.t('entity.serviceOrder.states.postponed'), colorScheme: 'neutral'})
    )
    .with(
      'CANCELLED',
      always({label: i18n.t('entity.serviceOrder.states.cancelled'), colorScheme: 'red'})
    )
    .with(
      'CHECKED_IN',
      always({label: i18n.t('entity.serviceOrder.states.checkedIn'), colorScheme: 'blue'})
    )
    .with(
      'HANDED_OVER',
      always({label: i18n.t('entity.serviceOrder.states.handedOver'), colorScheme: 'green'})
    )
    .with(
      'CREATED',
      always({label: i18n.t('entity.serviceOrder.states.created'), colorScheme: 'neutral'})
    )
    .with(Pattern.string, (unmatchedState) => ({label: unmatchedState}))
    .otherwise(always(undefined));

export function useStateFlag(state: StateType | Nullish) {
  return useMemo(() => {
    const flagProps = getStateFlag(state);

    return flagProps;
  }, [state]);
}
